"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Send,
  Mail,
  MessageSquare,
  Smartphone,
  FileText,
  Check,
} from "lucide-react";
import { NavigationHeader } from "@/components/common/NavigationHeader";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import { UserSelector } from "@/components/marketing/UserSelector";
import { toast } from "sonner";

interface CampaignForm {
  name: string;
  targetAudience: string[];
  selectedTemplate: string;
  channels: {
    email: boolean;
    sms: boolean;
    whatsapp: boolean;
  };
}

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  preview: string;
  channels: string[];
  image?: string;
  content?: string;
  template_type?: string;
  variables?: Record<string, any>;
  is_active?: boolean;
}

const CampaignFormPage = () => {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();

  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [templates, setTemplates] = useState<Template[]>([]);

  // Fetch marketing templates from API
  useEffect(() => {
    if (storeId) {
      fetchMarketingTemplates();
    }
  }, [storeId]);

  const fetchMarketingTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await fetch(
        `/api/v1/admin/${storeId}/communication/marketing-templates`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch marketing templates");
      }

      const data = await response.json();

      if (data.valid && data.data) {
        // Transform API response to our Template interface
        const transformedTemplates: Template[] = data.data
          .map((template: any) => ({
            id: template.id || template._id || Math.random().toString(36),
            name: template.name || template.title || "Untitled Template",
            description: template.description || "No description available",
            category: template.category || "General",
            preview:
              template.preview_text ||
              template.content?.substring(0, 150) + "..." ||
              "No preview available",
            channels: template.channels || ["Email"],
            image: template.image || "/api/placeholder/300/200",
            content: template.content,
            template_type: template.template_type,
            variables: template.variables,
            is_active: template.is_active !== false, // Default to true unless explicitly false
          }))
          .filter((template: Template) => template.is_active); // Only show active templates

        setTemplates(transformedTemplates);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching marketing templates:", error);
      toast.error("Failed to load templates. Using fallback templates.");

      // Fallback templates if API fails
      setTemplates([
        {
          id: "fallback-1",
          name: "Flash Sale Announcement",
          description:
            "Perfect for promoting limited-time offers and creating urgency",
          category: "Sales",
          preview:
            "🔥 Flash Sale Alert! Limited time offer - Get 50% off on all products. Shop now before it's too late!",
          channels: ["Email", "SMS", "WhatsApp"],
          image: "/api/placeholder/300/200",
        },
        {
          id: "fallback-2",
          name: "New Product Launch",
          description: "Introduce your latest products to customers",
          category: "Product",
          preview:
            "✨ Exciting News! We're thrilled to introduce our latest collection. Be the first to explore and shop now!",
          channels: ["Email", "WhatsApp"],
          image: "/api/placeholder/300/200",
        },
      ]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CampaignForm>({
    defaultValues: {
      name: "",
      targetAudience: [],
      selectedTemplate: "",
      channels: {
        email: false,
        sms: false,
        whatsapp: false,
      },
    },
  });

  const watchChannels = watch("channels");
  const watchTargetAudience = watch("targetAudience");
  const watchName = watch("name");

  const onSubmit = async (data: CampaignForm) => {
    setLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("Campaign data:", {
        ...data,
        selectedTemplate,
      });

      // Navigate back to campaigns list
      navigate(`/${storeType}/${storeId}/marketing/campaign`);
    } catch (error) {
      console.error("Failed to create campaign:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedChannels = () => {
    return Object.entries(watchChannels)
      .filter(([, enabled]) => enabled)
      .map(([channel]) => channel);
  };

  const isFormValid = () => {
    return (
      watchName &&
      watchTargetAudience &&
      watchTargetAudience.length > 0 &&
      selectedTemplate &&
      getSelectedChannels().length > 0
    );
  };

  const navigationItems = [
    {
      label: "Marketing",
      href: `/${storeType}/${storeId}/marketing`,
    },
    {
      label: "Campaign",
      href: `/${storeType}/${storeId}/marketing/campaign`,
    },
    {
      label: "New Campaign",
      isActive: true,
    },
  ];

  return (
    <div className="space-y-4">
      <NavigationHeader items={navigationItems} />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Create New Campaign</h1>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline">Save Draft</Button>
            <Button type="submit" disabled={loading || !isFormValid()}>
              <Send className="h-4 w-4 mr-2" />
              Send Campaign
            </Button>
          </div>
        </div>

        {/* Form Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Campaign Details */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-medium mb-4">Campaign Details</h3>

              <div className="space-y-4">
                <StandardFormInput
                  label="Campaign Name"
                  placeholder="e.g., Spring Sale Announcement"
                  error={errors.name?.message}
                  required
                  {...register("name", {
                    required: "Campaign name is required",
                  })}
                />

                <Controller
                  name="targetAudience"
                  control={control}
                  rules={{
                    required: "Target audience is required",
                    validate: (value) =>
                      (Array.isArray(value) && value.length > 0) ||
                      "Select at least one target audience",
                  }}
                  render={({ field }) => (
                    <UserSelector
                      label="Target Audience"
                        required
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="Select recipients for this campaign"
                      error={errors.targetAudience?.message}
                      helperText="Choose who will receive this campaign (multiple selections allowed)"
                      multiple={true}
                    />
                  )}
                />
              </div>
            </div>

            {/* Template Selection */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-medium mb-4">Choose Template</h3>

              {templatesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    <span className="text-sm text-muted-foreground">
                      Loading templates...
                    </span>
                  </div>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No templates available
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className={`relative border rounded-lg p-4 cursor-pointer transition-all bg-background ${
                        selectedTemplate === template.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                      onClick={() => {
                        setSelectedTemplate(template.id);
                        setValue("selectedTemplate", template.id);
                      }}
                    >
                      {selectedTemplate === template.id && (
                        <div className="absolute top-2 right-2">
                          <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-primary-foreground" />
                          </div>
                        </div>
                      )}

                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium">{template.name}</h4>
                          <p className="text-xs text-muted-foreground mb-2">
                            {template.description}
                          </p>
                          <div className="flex gap-1 mb-3">
                            {template.channels.map((channel) => (
                              <Badge
                                key={channel}
                                variant={channel.toLowerCase() as any}
                              >
                                {channel}
                              </Badge>
                            ))}
                          </div>
                          <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                            {template.preview}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!selectedTemplate && !templatesLoading && (
                <p className="text-red-500 text-xs mt-2">
                  Please select a template
                </p>
              )}
            </div>
          </div>

          {/* Sidebar Settings */}
          <div className="space-y-6">
            {/* Channel Selection */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-medium mb-4">Channels</h3>

              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Controller
                    name="channels.email"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="email"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <label
                      htmlFor="email"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Email
                    </label>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Controller
                    name="channels.sms"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="sms"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Smartphone className="h-4 w-4 text-green-600" />
                    <label
                      htmlFor="sms"
                      className="text-sm font-medium cursor-pointer"
                    >
                      SMS
                    </label>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Controller
                    name="channels.whatsapp"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="whatsapp"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <MessageSquare className="h-4 w-4 text-emerald-600" />
                    <label
                      htmlFor="whatsapp"
                      className="text-sm font-medium cursor-pointer"
                    >
                      WhatsApp
                    </label>
                  </div>
                </div>
              </div>

              {getSelectedChannels().length === 0 && (
                <p className="text-red-500 text-xs mt-2">
                  Select at least one channel
                </p>
              )}
            </div>

            {/* Campaign Summary */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-medium mb-4">Campaign Summary</h3>

              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium">Name:</span>
                  <p className="text-muted-foreground mt-1">
                    {watchName || "Not set"}
                  </p>
                </div>

                <div>
                  <span className="font-medium">Recipients:</span>
                  <div className="text-muted-foreground mt-1">
                    {watchTargetAudience && watchTargetAudience.length > 0 ? (
                      <div className="space-y-1">
                        {watchTargetAudience.map((audienceId: string) => {
                          // Mock data lookup - replace with actual data source
                          const audienceOptions = [
                            { id: "all_users", name: "All Users" },
                            {
                              id: "premium_customers",
                              name: "Premium Customers",
                            },
                            { id: "active_shoppers", name: "Active Shoppers" },
                            { id: "new_customers", name: "New Customers" },
                            { id: "john_doe", name: "John Doe" },
                            { id: "jane_smith", name: "Jane Smith" },
                            { id: "mike_johnson", name: "Mike Johnson" },
                          ];
                          const audience = audienceOptions.find(
                            (opt) => opt.id === audienceId,
                          );
                          return (
                            <div key={audienceId} className="text-sm">
                              • {audience?.name || audienceId}
                            </div>
                          );
                        })}
                        <div className="text-xs text-muted-foreground/75 mt-1">
                          {watchTargetAudience.length} audience
                          {watchTargetAudience.length !== 1 ? "s" : ""} selected
                        </div>
                      </div>
                    ) : (
                      "Not selected"
                    )}
                  </div>
                </div>

                <div>
                  <span className="font-medium">Template:</span>
                  <p className="text-muted-foreground mt-1">
                    {selectedTemplate
                      ? templates.find((t) => t.id === selectedTemplate)
                          ?.name || "Not selected"
                      : "Not selected"}
                  </p>
                </div>

                <div>
                  <span className="font-medium">Channels:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {getSelectedChannels().length > 0 ? (
                      getSelectedChannels().map((channel) => (
                        <Badge
                          key={channel}
                          variant={channel.toLowerCase() as any}
                        >
                          {channel.charAt(0).toUpperCase() + channel.slice(1)}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground text-xs">
                        None selected
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CampaignFormPage;
